import React, { useEffect, useRef, useState } from 'react';
import apiService from '../../services/ApiService';
import { useToast } from '../../contexts/ToastContext';
import { SUPPORTED_LANGUAGES, languageLabel } from '../../constants/languages';

interface ToolGuide { id: number; toolId: number; language: string; title?: string; fileName: string; fileSizeBytes: number; uploadedAt: string }

const formatSize = (b: number) =>
    b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1048576).toFixed(1)} MB`;

/**
 * PDF della guida scaricabili dal portale, uno per lingua. Resta finché la guida a blocchi
 * non genera il PDF da sola (fase E di TOOL_CONTENT_STUDIO.md).
 */
const GuidePdfCard: React.FC<{ toolId: number; onLanguagesChange: (languages: string[]) => void }> = ({ toolId, onLanguagesChange }) => {
    const { showToast } = useToast();
    const [guides, setGuides] = useState<ToolGuide[]>([]);
    const [language, setLanguage] = useState('it');
    const [title, setTitle] = useState('');
    const [busy, setBusy] = useState(false);
    const fileRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        apiService.adminGetToolGuides<ToolGuide>(toolId).then(setGuides).catch(() => setGuides([]));
    }, [toolId]);

    const update = (next: ToolGuide[]) => {
        setGuides(next);
        onLanguagesChange(next.map(g => g.language));
    };

    const upload = async () => {
        const file = fileRef.current?.files?.[0];
        if (!file) { showToast('Nessun file', 'Scegli il PDF da caricare', 'warning'); return; }
        setBusy(true);
        try {
            const saved = await apiService.adminUploadToolGuide(toolId, language, title || undefined, file) as ToolGuide;
            update([...guides.filter(g => g.language !== saved.language), saved].sort((a, b) => a.language.localeCompare(b.language)));
            setTitle('');
            if (fileRef.current) fileRef.current.value = '';
            showToast('Guida caricata', languageLabel(language), 'success');
        } catch (error) {
            const message = (error as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Riprova';
            showToast('Guida non caricata', message, 'danger');
        } finally {
            setBusy(false);
        }
    };

    const remove = async (guide: ToolGuide) => {
        if (!window.confirm(`Eliminare la guida PDF in ${languageLabel(guide.language)}?`)) return;
        await apiService.adminDeleteToolGuide(toolId, guide.id);
        update(guides.filter(g => g.id !== guide.id));
    };

    return (
        <section className="td-card td-pdf" aria-labelledby="td-pdf-title">
            <h2 id="td-pdf-title" className="td-card__title">Guide PDF scaricabili</h2>
            <p className="td-card__lead">Il portale offre il PDF nella lingua dell’utente. Caricarne uno nuovo sostituisce quello della stessa lingua.</p>
            {guides.length > 0 && (
                <ul className="td-pdf__list">
                    {guides.map(g => (
                        <li key={g.id}>
                            <span className="td-pdf__lang">{g.language.toUpperCase()}</span>
                            <span className="td-pdf__name"><strong>{g.title || g.fileName}</strong><span>{formatSize(g.fileSizeBytes)}</span></span>
                            <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => void remove(g)}
                                aria-label={`Elimina la guida in ${languageLabel(g.language)}`}><i className="bi bi-trash" aria-hidden="true" /></button>
                        </li>
                    ))}
                </ul>
            )}
            <div className="td-pdf__form">
                <div className="td-field">
                    <label htmlFor="td-pdf-lang">Lingua</label>
                    <select id="td-pdf-lang" className="form-select form-select-sm" value={language} onChange={e => setLanguage(e.target.value)}>
                        {SUPPORTED_LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
                    </select>
                </div>
                <div className="td-field">
                    <label htmlFor="td-pdf-title">Titolo (facoltativo)</label>
                    <input id="td-pdf-title" className="form-control form-control-sm" value={title} onChange={e => setTitle(e.target.value)} />
                </div>
                <div className="td-field">
                    <label htmlFor="td-pdf-file">File PDF</label>
                    <input id="td-pdf-file" ref={fileRef} type="file" accept="application/pdf" className="form-control form-control-sm" />
                </div>
                <button type="button" className="btn btn-primary btn-sm" onClick={() => void upload()} disabled={busy}>
                    {busy ? 'Carico…' : 'Carica'}
                </button>
            </div>
        </section>
    );
};

export default GuidePdfCard;
